'use client';

import React, { Suspense } from 'react';
import Loading from '@/app/shared/common/Loading';
import dynamic from 'next/dynamic';
import ContactUsPopup from '@/app/layout/popup/ContactUsPopup';

// Static imports for Next.js image optimization
import heroDesktop from '../../../public/images/hero-desktop.webp';
import heroDesktopHres from '../../../public/images/hero-desktop-hres.webp';
import heroTablet from '../../../public/images/hero-tablet.webp';
import heroMobile from '../../../public/images/hero-mobile-lres.webp';

interface LandingHeroProps {
  titleText?: string;
  supportingText?: string;
  buttonText?: string;
  buttonHref?: string;
  scrollDownSectionRef?: string;
}

// Dynamic imports with error handling
const ParallaxBackground = dynamic(
  () =>
    import('@/app/shared/common/ParallaxBackground').catch((err) => {
      console.error('Failed to load ParallaxBackground:', err);
      // Fallback to a simple div with the image as background
      const FallbackComponent = () => (
        <div
          className='w-full h-full bg-cover bg-center'
          style={{ backgroundImage: `url(${heroDesktop.src})` }}
        />
      );
      FallbackComponent.displayName = 'ParallaxBackgroundFallback';
      return FallbackComponent;
    }),
  {
    loading: () => <Loading />,
    ssr: false, // Disable SSR for image-heavy component
  }
);
ParallaxBackground.displayName = 'DynamicParallaxBackground';

// Load MainHero without SSR to improve LCP
const MainHero = dynamic(
  () =>
    import('./MainHero').catch((err) => {
      console.error('Failed to load MainHero:', err);
      const ErrorComponent = () => <div>Error loading content</div>;
      ErrorComponent.displayName = 'MainHeroError';
      return ErrorComponent;
    }),
  {
    loading: () => <Loading />,
    ssr: false,
  }
);
MainHero.displayName = 'DynamicMainHero';

function LandingHero(props: LandingHeroProps) {
  const defaultProps = {
    titleText: 'Designer Series',
    supportingText:
      'An innovative modular recognition system combining flexibility, style, and affordability',
    buttonText: 'Discover Now',
    buttonHref: '/features',
    scrollDownSectionRef: '#welcome',
  };

  const heroProps = { ...defaultProps, ...props };
  return (
    <div
      className='relative w-full min-h-screen overflow-hidden'
      suppressHydrationWarning
    >
      <Suspense fallback={<Loading />}>
        <ParallaxBackground
          type='image'
          staticSources={{
            desktop: heroDesktop,
            desktopHres: heroDesktopHres,
            tablet: heroTablet,
            mobile: heroMobile,
          }}
          objectPosition={{
            mobile: 'left',
            tablet: 'left',
            desktop: 'left',
          }}
          isAboveFold={true}
          disableOnMobile={true}
          parallaxSpeed={10}
          parallaxSpeedMobile={5}
          scaleOnScroll={false}
          className='relative w-full min-h-screen h-full top-0'
          imgClassName='object-cover w-full h-full scale-100 object-center'
          alt='Designer Series Recognition Board'
        >
          <Suspense fallback={<Loading />}>
            <MainHero {...heroProps} />
          </Suspense>
        </ParallaxBackground>
      </Suspense>
      <ContactUsPopup />
    </div>
  );
}

export default LandingHero;
